import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { toast } from 'sonner';
import { CronberryLogo } from '@/components/CronberryLogo';
import { ArrowLeft, Key, Lock } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passcode, setPasscode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetMethod, setResetMethod] = useState(tokenFromUrl ? 'link' : 'passcode');

  useEffect(() => {
    if (tokenFromUrl) {
      setResetMethod('link');
    }
  }, [tokenFromUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (resetMethod === 'passcode' && passcode.length !== 6) {
      toast.error('Please enter the complete 6-digit passcode');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        new_password: newPassword,
      };

      if (resetMethod === 'link' && tokenFromUrl) {
        payload.token = tokenFromUrl;
      } else if (resetMethod === 'passcode') {
        payload.passcode = passcode;
      } else {
        toast.error('Please provide either a reset link or passcode');
        setLoading(false);
        return;
      }

      await axios.post(`${API}/auth/reset-password`, payload);

      toast.success('Password reset successfully! You can now login with your new password.');
      
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-8">
          <div className="flex flex-col items-center mb-8">
            <div className="mb-4">
              <CronberryLogo className="h-24 w-auto" />
            </div>
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-center text-[#0B1F3A]">
              Reset Your Password
            </h1>
            <p className="text-slate-600 text-center mt-2">
              Enter your new password below
            </p>
          </div>

          <Tabs value={resetMethod} onValueChange={setResetMethod} className="mb-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="link" disabled={!tokenFromUrl}>
                Reset Link
              </TabsTrigger>
              <TabsTrigger value="passcode">Passcode</TabsTrigger>
            </TabsList>

            <TabsContent value="link" className="space-y-4 mt-4">
              {tokenFromUrl ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800 flex items-center">
                    <Key className="w-4 h-4 mr-2" />
                    Reset link detected. You can proceed to reset your password.
                  </p>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    No reset link found. Please use the passcode method or click the reset link from your email.
                  </p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="passcode" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="passcode">Enter 6-Digit Passcode</Label>
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={passcode}
                    onChange={setPasscode}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <p className="text-xs text-slate-500 text-center">
                  Enter the 6-digit code sent to your email
                </p>
              </div>
            </TabsContent>
          </Tabs>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
              />
              <p className="text-xs text-slate-500">
                Password must be at least 6 characters long
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#0B1F3A] hover:bg-[#0B1F3A]/90"
              disabled={loading || (resetMethod === 'passcode' && passcode.length !== 6)}
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </Button>

            <Link to="/login">
              <Button variant="ghost" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </form>
        </div>
      </div>
    </div>
  );
}
